import './styles.css';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, ConfigProvider, Skeleton } from 'antd';
import { CheckCircleFilled, ClockCircleFilled, CloseCircleFilled, LoadingOutlined, CreditCardOutlined } from '@ant-design/icons';
import { ordersSelectors, fetchOrder } from '../../features/orders';
import TinkoffCreditButton from '../../components/TinkoffCreditButton';

const getPaymentParams = (searchParams) => ({
    Success: searchParams.get('Success'),
    ErrorCode: searchParams.get('ErrorCode'),
    OrderId: searchParams.get('OrderId'),
});

const OrderSuccess = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { orderId: routeOrderId } = useParams();
    const [searchParams] = useSearchParams();

    const paymentParams = getPaymentParams(searchParams);
    const tinkoffSuccess = paymentParams.Success;
    const tinkoffErrorCode = paymentParams.ErrorCode;
    const tinkoffOrderId = paymentParams.OrderId;

    const orderId = routeOrderId || tinkoffOrderId;

    const isPaymentRedirect = tinkoffSuccess !== null;
    const paymentSuccessFromUrl = tinkoffSuccess === 'true' && tinkoffErrorCode === '0';
    const paymentFailedFromUrl = isPaymentRedirect && !paymentSuccessFromUrl;

    const lastOrder = useSelector(ordersSelectors.selectLastOrder);
    const paymentUrl = useSelector((state) => ordersSelectors.selectPaymentUrl(state, orderId));
    const [loading, setLoading] = useState(!!orderId);
    const [countdown, setCountdown] = useState(null);
    const pollingRef = useRef(null);
    const pollCountRef = useRef(0);
    const countdownRef = useRef(null);

    useEffect(() => {
        if (orderId) {
            setLoading(true);
            dispatch(fetchOrder(orderId)).finally(() => setLoading(false));
        }
    }, [dispatch, orderId]);

    const shouldAutoRedirect = !isPaymentRedirect && paymentUrl && !loading;

    useEffect(() => {
        if (shouldAutoRedirect) {
            setCountdown(3);
            countdownRef.current = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdownRef.current);
                        window.location.href = paymentUrl;
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [shouldAutoRedirect, paymentUrl]);

    const handlePayNow = useCallback(() => {
        if (countdownRef.current) clearInterval(countdownRef.current);
        window.location.href = paymentUrl;
    }, [paymentUrl]);

    const orderNumber = lastOrder?.order_number;
    const orderTotal = lastOrder?.total;
    const orderStatus = lastOrder?.status;
    const orderPaymentMethod = lastOrder?.payment_method;

    const isPaid = orderStatus === 'processing' || orderStatus === 'completed';
    const needsPayment = orderStatus === 'pending' || orderStatus === 'on-hold';

    useEffect(() => {
        if (paymentSuccessFromUrl && needsPayment && orderId && !isPaid) {
            pollingRef.current = setInterval(() => {
                pollCountRef.current += 1;
                dispatch(fetchOrder(orderId));

                if (pollCountRef.current >= 12) {
                    clearInterval(pollingRef.current);
                }
            }, 5000);
        }

        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [paymentSuccessFromUrl, needsPayment, isPaid, orderId, dispatch]);

    useEffect(() => {
        if (isPaid && pollingRef.current) {
            clearInterval(pollingRef.current);
        }
    }, [isPaid]);

    const statusLabels = {
        'pending': 'Ожидает оплаты',
        'processing': 'В обработке',
        'on-hold': 'На удержании',
        'completed': 'Выполнен',
        'cancelled': 'Отменён',
        'refunded': 'Возвращён',
        'failed': 'Не удался',
    };

    if (loading) {
        return (
            <div className="order-success-page">
                <div className="order-success-card">
                    <Skeleton active paragraph={{ rows: 4 }} title={{ width: '60%' }} />
                </div>
            </div>
        );
    }

    if (!orderId) {
        return (
            <div className="order-success-page">
                <div className="order-success-card">
                    <div className="order-success-title">Заказ не найден</div>
                    <div className="order-success-subtitle">
                        Информация о заказе недоступна
                    </div>
                    <div className="order-success-actions">
                        <Button type="primary" onClick={() => navigate('/')}>
                            На главную
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    let icon, title, subtitle;

    if (isPaid) {
        icon = <CheckCircleFilled className="order-success-icon" />;
        title = 'Оплата прошла успешно!';
        subtitle = 'Спасибо за покупку. Оплата получена, мы уже начали обработку вашего заказа.';
    } else if (paymentSuccessFromUrl && needsPayment) {
        icon = <LoadingOutlined className="order-success-icon order-verifying-icon" />;
        title = 'Проверяем оплату...';
        subtitle = 'Платёж обрабатывается. Статус заказа обновится автоматически.';
    } else if (paymentFailedFromUrl) {
        icon = <CloseCircleFilled className="order-success-icon order-failed-icon" />;
        title = 'Оплата не прошла';
        subtitle = 'К сожалению, платёж не был завершён. Попробуйте оформить заказ заново.';
    } else if (needsPayment && paymentUrl) {
        icon = <CreditCardOutlined className="order-success-icon order-pending-icon" />;
        title = 'Заказ оформлен!';
        subtitle = countdown !== null
            ? `Переход к оплате через ${countdown} сек...`
            : 'Переходим к оплате...';
    } else if (needsPayment && orderPaymentMethod === 'cod') {
        icon = <CheckCircleFilled className="order-success-icon" />;
        title = 'Заказ оформлен!';
        subtitle = 'Вы оплатите заказ при получении. Мы уже начали его обработку.';
    } else if (needsPayment && orderPaymentMethod === 'cheque') {
        icon = <CreditCardOutlined className="order-success-icon order-pending-icon" />;
        title = 'Заказ оформлен!';
        subtitle = 'Оформите оплату частями или в кредит — заявку рассмотрят за пару минут.';
    } else if (needsPayment) {
        icon = <ClockCircleFilled className="order-success-icon order-pending-icon" />;
        title = 'Заказ оформлен';
        subtitle = 'Заказ ожидает оплаты.';
    } else {
        icon = <CheckCircleFilled className="order-success-icon" />;
        title = 'Заказ оформлен!';
        subtitle = 'Спасибо за покупку. Мы уже начали обработку вашего заказа.';
    }

    return (
        <ConfigProvider
            theme={{
                token: { colorPrimary: '#D00202' },
                components: { Button: { colorPrimaryHover: '#ff3333' } }
            }}
        >
            <div className="order-success-page">
                <div className="order-success-card">
                    {icon}
                    <div className="order-success-title">{title}</div>
                    <div className="order-success-subtitle">{subtitle}</div>

                    {paymentSuccessFromUrl && needsPayment && !isPaid && (
                        <div className="order-payment-waiting">
                            <LoadingOutlined style={{ fontSize: 20, marginRight: 8 }} />
                            <span>Ожидаем подтверждение от банка...</span>
                        </div>
                    )}

                    <div className="order-success-details">
                        {(orderNumber || orderId) && (
                            <div className="order-detail-row">
                                <span className="order-detail-label">Номер заказа</span>
                                <span className="order-detail-value">#{orderNumber || orderId}</span>
                            </div>
                        )}
                        {orderStatus && (
                            <div className="order-detail-row">
                                <span className="order-detail-label">Статус</span>
                                <span className="order-detail-value">
                                    {needsPayment && orderPaymentMethod === 'cod'
                                        ? 'Оформлен, оплата при получении'
                                        : statusLabels[orderStatus] || orderStatus}
                                </span>
                            </div>
                        )}
                        {orderTotal && (
                            <div className="order-detail-row">
                                <span className="order-detail-label">Сумма</span>
                                <span className="order-detail-value">
                                    {Math.round(Number(orderTotal)).toLocaleString('ru-RU')}₽
                                </span>
                            </div>
                        )}
                        {lastOrder?.date_created && (
                            <div className="order-detail-row">
                                <span className="order-detail-label">Дата</span>
                                <span className="order-detail-value">
                                    {new Date(lastOrder.date_created).toLocaleDateString('ru-RU')}
                                </span>
                            </div>
                        )}
                    </div>

                    {needsPayment && orderPaymentMethod === 'cheque' && !isPaid && (
                        <div className="order-credit-block">
                            <TinkoffCreditButton order={lastOrder} />
                        </div>
                    )}

                    <div className="order-success-actions">
                        {needsPayment && paymentUrl && !isPaymentRedirect && (
                            <Button
                                type="primary"
                                icon={<CreditCardOutlined />}
                                onClick={handlePayNow}
                                className="order-pay-now-btn"
                            >
                                Оплатить сейчас
                            </Button>
                        )}
                        <Button type={needsPayment && paymentUrl && !isPaymentRedirect ? 'default' : 'primary'} onClick={() => navigate('/')}>
                            На главную
                        </Button>
                        <Button onClick={() => navigate('/profile')}>
                            Мои заказы
                        </Button>
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default OrderSuccess;
